<template>
  <div :class="['screenerQuestionsWrapper', { hide: fadeComponent }]">
    <MButton
      v-if="DEV_STORE.getNextStepReady()"
      title="Next Step"
      variant="primary"
      size="lg"
      button-style="position: fixed; top: 2rem; left: 50%; transform: translateX(-50%); z-index: 1000;"
      @click="manualNextStep"
    />

    <BackgroundComponent
      v-if="!rewardsDisabled"
      :key="demoBackgroundKey"
      :currentScene="currentScene"
      :backgroundTrackVolume="backgroundTrackVolume"
    />

    <SceneEffectsComponent @animation-finished="onSceneAnimationFinished" />

    <AvatarComponent
      v-if="!hideAvatar && (avatarOptions || !screenerAvatar)"
      ref="avatar"
      :key="`avatar-${dataStore.demoAvatarKey}`"
      :options="!!(avatarOptions && screenerAvatar) ? avatarOptions : undefined"
      :delay="avatarDelay"
      :isAnimating="!!(avatarOptions && screenerAvatar)"
      @avatar-finished="onAvatarFinished"
      @walking-complete="startSubtestRewardAnimation"
    />

    <RecordingComponent
      v-if="screenerData?.metadata?.mediaSettings?.requiresRecording !== false"
      ref="recorder"
      :record="record"
      :wait-time="DEV_STORE.skipWaitTime ? 0 : dataStore.effectiveWaitTime"
      :is-practice-question="currentQuestion!.isPracticeQuestion"
      :question-id="currentQuestion!.id"
      :waiting-for-answer="waitingForAnswer"
      :proceedButton="showButton"
      @recording-finished="onAudioAnswerGiven"
      @play-prompt="playPrompt"
    />

    <Transition name="slide-fade" appear>
      <ImageSelectionComponent
        v-if="showSelectionComponent"
        :options="currentOptions"
        :question-id="currentQuestion!.id"
        :wait-time="DEV_STORE.skipWaitTime ? 0 : dataStore.effectiveWaitTime"
        :waiting-for-answer="waitingForAnswer || awaitingAvatarSelection"
        :is-practice-question="currentQuestion!.isPracticeQuestion"
        :is-avatars-turn="isAvatarsTurn"
        @play-prompt="playPrompt"
        @image-selected="onSelection"
      />
    </Transition>

    <Transition name="fade" appear>
      <QuestionnaireComponent
        v-if="showQuestionnaireComponent"
        :is-last-question="isLastQuestion"
        :current-question="currentQuestion as QuestionEntity"
        :waiting-for-answer="waitingForAnswer"
        @answer-given="onQuestionnaireAnswerGiven"
      />
    </Transition>

    <Transition name="video-fade" appear>
      <AssetComponent
        v-if="showAssetComponent"
        :asset-url="currentAssetUrl"
        :asset-type="currentAssetType"
        :play-video="playVideoRef"
        :transition-duration="videoTransitionDuration"
        @video-ended="onVideoEnded"
      />
    </Transition>

    <QuestionRewardComponent
      v-if="!rewardsDisabled && showQuestionRewardComponent"
      :clickable="questionRewardClickable"
      :first-load="introSequencePlaying"
      :questionReward="screenerData?.metadata?.questionReward"
      @reward-collected="onQuestionRewardCollected"
    />

    <Transition name="fade" appear>
      <QuestionCue
        v-if="showQuestionCue && currentQuestion!.waitTime !== null"
        ref="questionCue"
        :wait-time="DEV_STORE.skipWaitTime ? 0 : dataStore.effectiveWaitTime"
        :waiting-for-answer="waitingForAnswer"
        :is-recording="record"
        :force-active="forceCueActiveState"
        :is-avatars-turn="isAvatarsTurn && currentScene !== 'Intro'"
      />
    </Transition>

    <Transition name="fade" appear>
      <WellbeingSlides
        v-if="showWellbeingSlides"
        :currentSlide="activeWellbeingSlide"
        :timestamps="activeWellbeingTimestamps"
      />
    </Transition>

    <transition name="fade">
      <WellbeingProgress v-if="showWellbeingProgress" />
    </transition>

    <SubtestRewardComponent
      v-if="!rewardsDisabled && showSubtestRewardComponent"
      ref="subtestRewardComponent"
      @animation-finished="onSceneAnimationFinished"
    />

    <Transition name="bounce">
      <NextQuestionButton
        v-if="showNextQuestionBtn && waitingForAnswer"
        :background-theme="currentSubtest?.metadata?.backgroundTheme || 'light'"
        @next-question-clicked="onProceedTriggered"
        :waiting-for-answer="waitingForAnswer"
      />
    </Transition>
    <Transition name="fade" appear>
      <StarPop v-if="showStarPopGameComponent" @game-finished="onStarPopGameFinished" />
    </Transition>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import {
  computed,
  onMounted,
  ref,
  onBeforeMount,
  nextTick,
  provide,
  onBeforeUnmount,
  watch
} from 'vue'
import type { Ref } from 'vue'
import type {
  AvatarOptions,
  AvatarPosition,
  AvatarSize,
  QuestionEntity,
  SubtestEntity,
  OptionsEntity,
  Steps,
  AssetType,
  AvatarAnimation,
  QuestionTimestamp
} from '@/customTypes'
import Logger from '@/services/Logger.js'

// Store
import { useDataStore } from '@/stores/DataStore'
import { useEffectsStore } from '@/stores/EffectsStore'
import { useDevStore } from '@/stores/DevStore'
import { useSoundEffectsStore } from '@/stores/SoundEffectsStore'
import { usePreloadStore } from '@/stores/PreloadStore'
import { useActivityStore } from '@/stores/ActivityStore'
import { useActiveScreenerStore } from '@/stores/activeScreenerStore'

// Components
import AvatarComponent from '@/components/AvatarComponent.vue'
import RecordingComponent from '@/components/RecordingComponent.vue'
import SubtestRewardComponent from '@/components/SubtestRewardComponent.vue'
import QuestionRewardComponent from '@/components/QuestionRewardComponent.vue'
import NextQuestionButton from '@/components/NextQuestionButton.vue'
import ImageSelectionComponent from '@/components/ImageSelectionComponent.vue'
import QuestionnaireComponent from '@/components/QuestionnaireComponent.vue'
import AssetComponent from '@/components/AssetComponent.vue'
import SceneEffectsComponent from '@/components/SceneEffectsComponent.vue'
import BackgroundComponent from '@/components/BackgroundComponent.vue'
import QuestionCue from '@/components/QuestionCue.vue'
import MButton from '@/components/MButton.vue'
import FullScreen from '@/services/FullScreen.js'
import WellbeingSlides from '@/components/WellbeingSlides.vue'
import WellbeingProgress from '@/components/WellbeingProgress.vue'
import StarPop from '@/components/games/starPop/StarPop.vue'

// Helpers
import { useTimeout } from '@/utils/helpers'

const logger: any = new Logger('ScreenerQuestionsView', true)
const { setManagedTimeout } = useTimeout()

const introSequenceFunction: Record<
  string,
  { intro: () => Promise<void> | void; outro?: () => Promise<void> | void }
> = {
  'salt-screener-intro-sequence': { intro: saltScreenerIntroStep1 },
  'wellbeing-screener-intro-sequence': {
    intro: wellbeingScreenerIntro,
    outro: wellbeingScreenerOutro
  }
}

// DATA
const steps: Steps = {
  'audio-recording': {
    promptFunction: playAudioPromptWithQuestion,
    1: firstStep,
    2: playQuestionIntro,
    3: changeBackgroundVolume.bind(null, true, true),
    4: initiateRecording,
    5: playQuestion,
    6: waitForAudioAnswer,
    7: playOutro,
    8: playStarPopGame,
    9: finalStep
  },
  'selection-select': {
    promptFunction: playSelectionPrompt,
    1: firstStep,
    2: changeBackgroundVolume.bind(null, true, true),
    3: setBlur.bind(null, true),
    4: showSelection,
    5: playQuestion,
    6: waitForSelection,
    7: playOutro,
    8: finalStep
  },
  'video-recording': {
    promptFunction: playVideoPrompt,
    1: firstStep,
    2: playQuestionIntro,
    3: changeBackgroundVolume.bind(null, true, true),
    4: repositionAvatar,
    5: setBlur.bind(null, true),
    6: initiateRecording,
    7: showAsset.bind(null, 'video'),
    8: playQuestion,
    9: waitForAudioAnswer,
    10: playOutro,
    11: playStarPopGame,
    12: finalStep
  },
  'image-recording': {
    promptFunction: playAudioPromptWithQuestion,
    1: firstStep,
    2: playQuestionIntro,
    3: changeBackgroundVolume.bind(null, true, true),
    4: setBlur.bind(null, true),
    5: initiateRecording,
    6: playQuestion,
    7: showAsset.bind(null, 'image'),
    8: waitForAudioAnswer,
    9: playOutro,
    10: finalStep
  },
  'questionnaire-select': {
    promptFunction: () => {},
    1: firstStep,
    2: showQuestionnaire,
    3: playQuestion,
    4: waitForSelection,
    5: finalStep
  }
}

const preloadStore = usePreloadStore()
const dataStore = useDataStore()
const {
  currentSubtest,
  currentSubtestAnswers,
  currentQuestion,
  screenerData,
  pupilAnswers,
  backgroundVolumeFadeLevel,
  backgroundVolumeLevel,
  screenerAvatar,
  pausedState,
  screenerIntroPlayed,
  demoAvatarKey,
  demoBackgroundKey,
  skipTriggered,
  demoSkipTo
} = storeToRefs(dataStore) // reactive ref from store

const effectsStore = useEffectsStore()
const soundEffectsStore = useSoundEffectsStore()
const activityStore = useActivityStore()
const DEV_STORE = useDevStore()
const activeScreenerStore = useActiveScreenerStore()

const { fadeDuration } = storeToRefs(soundEffectsStore)
const { avatarAnimationState, avatarAnimationQueue } = storeToRefs(effectsStore)
const avatarPromptAnimation = effectsStore.getPromptAnimation()

// Will trigger the fade out of the component at the end of the screener
const fadeComponent: Ref<boolean> = ref(false)
const avatar: Ref<InstanceType<typeof AvatarComponent> | null> = ref(null)
const questionCue: Ref<InstanceType<typeof QuestionCue> | null> = ref(null)
const subtestRewardComponent: Ref<InstanceType<typeof SubtestRewardComponent> | null> = ref(null)

const introSequencePlaying: Ref<boolean> = ref(false)
const forceCueActiveState: Ref<boolean> = ref(false) // Used to force the cue to be active in the intro sequence

const currentSubtestId: Ref<number | null> = ref(null) // Used to determine if we need to play the subtest intro
const currentOptions: Ref<OptionsEntity[]> = ref([])
const currentAssetUrl: Ref<string> = ref('')
const currentAssetType: Ref<AssetType> = ref('video')
const isLastQuestion: Ref<boolean> = ref(false)
const startsThisSubtest: Ref<number> = ref(0)
const currentScene: Ref<string> = ref('')

// Transition durations
const videoTransitionDuration: number = 1000
const avatarTransitionDuration: number = 750
const fadeComponentDuration: number = 3000
let fadeComponentDelay: number = 2000
const slideTransitionDuration: number = 500
// State of sequence
const currentStep: Ref<number> = ref(1)

const record: Ref<boolean> = ref(false)
const waitingForAnswer: Ref<boolean> = ref(false)
const answerGiven: Ref<boolean> = ref(false)
const recorder: Ref<InstanceType<typeof RecordingComponent> | null> = ref(null)

// Callbacks
const avatarCallback: Ref<Function | null> = ref(stepFinished)
const videoEndedCallback: Ref<Function> = ref(stepFinished)
const rewardCollectedCallback: Ref<Function> = ref(stepFinished)

// Audio answer properties
const isPlayingPrompt: Ref<boolean> = ref(false)
const playVideoRef: Ref<boolean> = ref(false)

// Avatar and scene properties
const avatarOptions: Ref<AvatarOptions | null> = ref(null)
const hideAvatar: Ref<boolean> = ref(false)
provide('avatarOptions', avatarOptions)

const avatarDelay: Ref<number> = ref(1)
const avatarFinished: Ref<boolean> = ref(false)
const sceneAnimationPlaying: Ref<boolean> = ref(false)

// Sub-components states
const showSubtestRewardComponent: Ref<boolean> = ref(false)
const showQuestionRewardComponent: Ref<boolean> = ref(false)
const questionRewardClickable: Ref<boolean> = ref(false)
const showNextQuestionBtn: Ref<boolean> = ref(false)
const showSelectionComponent: Ref<boolean> = ref(false)
const showAssetComponent: Ref<boolean> = ref(false)
const showQuestionCue: Ref<boolean> = ref(false)
const showQuestionnaireComponent: Ref<boolean> = ref(false)
const showWellbeingSlides: Ref<boolean> = ref(false)
const showWellbeingProgress: Ref<boolean> = ref(false)
const showStarPopGameComponent: Ref<boolean> = ref(false)

const backgroundTrackVolume: Ref<number> = ref(backgroundVolumeLevel.value)

const emit = defineEmits<{
  (e: 'screening-finished'): void
  (e: 'screening-paused'): void
}>()

// Sequence name created from the current subtest question type and answer type
const sequenceName: Ref<string> = computed(() => {
  if (!currentSubtest.value?.questionType || !currentSubtest.value?.answerType) return ''

  return `${currentSubtest.value.questionType}-${currentSubtest.value.answerType}`
})

const rewardsDisabled: Ref<boolean> = computed(() => {
  return screenerData.value?.metadata?.additionalSettings?.disableRewards || false
})

const isAvatarsTurn = computed(() => {
  return !!(
    currentQuestion.value?.isPracticeQuestion &&
    currentQuestion.value?.avatarAnswerUrl?.endsWith('_A.mp3')
  )
})

const isAvatarPrompting = computed(() => {
  return (
    avatarPromptAnimation &&
    (avatarAnimationState.value === avatarPromptAnimation ||
      avatarAnimationQueue.value.includes(avatarPromptAnimation))
  )
})

// Lifecycle
onBeforeMount(() => {
  if (currentSubtestAnswers.value.length > 0) {
    currentSubtestId.value = currentSubtest.value!.id // Set the current subtest id to skip subtest intro
    setAvatarPosition(
      currentSubtest.value!.metadata?.questionAvatarPosition,
      currentSubtest.value!.metadata?.questionAvatarSize
    )
    setAvatarAudio('')
  }
  document.removeEventListener('click', checkFullscreen)
})

onMounted(() => {
  setBlur(false, false)
  soundEffectsStore.toggleMuted(false)
  document.addEventListener('click', checkFullscreen)
  if (demoSkipTo.value === 'reward' && steps[sequenceName.value]) {
    // Set to be the final step of the sequence
    dataStore.completedCurrentQuestion()
    stepFinished()
    return
  }
  nextStep()
})

onBeforeUnmount(() => {
  soundEffectsStore.stopTracks()
  document.removeEventListener('click', checkFullscreen)
})

watch(pausedState, (paused) => {
  if (paused) {
    if (avatar.value) {
      setAvatarAudio('')
      avatarFinished.value = true
      avatar.value?.stop()
    }
    soundEffectsStore.stopTracks()
    emit('screening-paused')
  }
})

watch(skipTriggered, (newValue) => {
  if (newValue) {
    handleSkip()
    dataStore.resetSkipTrigger()
  }
})

function handleSkip() {
  if (avatar.value) {
    setAvatarAudio('')
    avatar.value.stop()
  }
  avatarFinished.value = true
  soundEffectsStore.stopTracks()

  if (waitingForAnswer.value) {
    onAudioAnswerGiven(true)
  } else {
    if (typeof avatarCallback.value === 'function') {
      avatarCallback.value()
      avatarCallback.value = stepFinished
    } else {
      nextStep()
    }
  }
}

watch(demoAvatarKey, () => {
  // Stop any ongoing avatar animations or audio and reset avatar state
  if (avatar.value) {
    avatar.value.stop()
  }

  avatarFinished.value = false
})

function checkFullscreen() {
  if (!DEV_STORE.forceFullscreen) return
  if (!FullScreen.isEnabled() && !showStarPopGameComponent.value) FullScreen.toggleAll()
}

// _____________________ SEQUENCE FUNCTIONS _____________________
function onAvatarFinished() {
  avatarFinished.value = true
  if (!avatarCallback.value || sceneAnimationPlaying.value || pausedState.value) return
  avatarCallback.value()
}

function onSceneAnimationFinished() {
  sceneAnimationPlaying.value = false
  if (!avatarCallback.value || !avatarFinished.value || pausedState.value) return

  avatarCallback.value()
}

function nextStep() {
  if (sequenceName.value && !steps[sequenceName.value]) {
    logger.error('Sequence not found', sequenceName.value)
    return
  }

  if (DEV_STORE.stopOnNextStep) {
    DEV_STORE.setNextStepReady(true)
    return
  }

  if (!sequenceName.value || pausedState.value) return
  // dynamically call the next step function based on the current step number
  steps[sequenceName.value][currentStep.value]()
}

function manualNextStep() {
  DEV_STORE.setNextStepReady(false)
  steps[sequenceName.value][currentStep.value]()
}

function stepFinished(stepsToMove: number = 1): void {
  currentStep.value += stepsToMove
  nextStep()
}

function resetSequence(): void {
  logger.info('Reset sequence')
  currentStep.value = 0
  answerGiven.value = false
}

async function firstStep(): Promise<void> {
  if (demoSkipTo.value) {
    if (demoSkipTo.value === 'outro') {
      playScreenerOutro()
      dataStore.resetDemoSkippingTo()
    }

    return
  }

  if (currentQuestion.value?.completed) {
    finalStep()
    return
  }

  setRecording()

  if (
    !screenerIntroPlayed.value &&
    (!pupilAnswers?.value || pupilAnswers.value.length === 0) &&
    DEV_STORE.skipScreenerIntro !== true // check if screener intro is skipped through dev tools
  ) {
    introSequencePlaying.value = true
    introSequenceFunction[screenerData.value?.metadata?.intro?.sequenceName].intro()
    return
  }

  introSequencePlaying.value = false
  isLastQuestion.value = dataStore.checkIfLastQuestion(
    currentQuestion.value as QuestionEntity,
    currentSubtest.value as SubtestEntity
  )

  if (isLastQuestion.value && currentQuestion.value?.isPracticeQuestion === false) {
    preloadStore.preloadNextSubtest()
  }

  effectsStore.setNextBackgroundImage(currentSubtest.value?.metadata?.backgroundImage)
  effectsStore.setAvatarTransitionCallback(setBackgroundImage)
  currentScene.value = `S${currentSubtest.value?.id}`

  if (currentSubtestId.value === currentSubtest.value!.id || DEV_STORE.skipSubtestIntro === true) {
    stepFinished()
    return
  } else {
    playSubtestIntro()
  }
}

function setBackgroundImage() {
  effectsStore.applyBackgroundImage()
  effectsStore.setAvatarTransitionCallback(null, null)
  if (showSubtestRewardComponent.value === true) showSubtestRewardComponent.value = false
}

function finalizeScreening() {
  fadeComponent.value = true
  setManagedTimeout(() => {
    emit('screening-finished')
  }, fadeComponentDuration + fadeComponentDelay)
}

// _____________________ AVATAR FUNCTIONS _____________________

async function playSubtestIntro(): Promise<void> {
  logger.info('Play subtest intro')
  changeBackgroundVolume(false, false)
  await setAvatarPosition(
    currentSubtest.value!.metadata?.introAvatar?.position,
    currentSubtest.value!.metadata?.introAvatar?.size
  )

  if (!currentSubtest.value!.introUrl) {
    logger.info('No subtest intro found, skipping...')
    currentSubtestId.value = currentSubtest.value!.id
    stepFinished()
    return
  }

  avatarDelay.value = 1
  startsThisSubtest.value = 0
  setAvatarAudio(currentSubtest.value!.introUrl)

  effectsStore.setAnimationTriggers(currentSubtest.value!.metadata?.animationTriggers || [])
  effectsStore.setLoadedSceneAnimations(currentSubtest.value!.metadata?.sceneAnimations || [])
  currentSubtestId.value = currentSubtest.value!.id
}

async function playQuestionIntro(): Promise<void> {
  if (!currentQuestion.value!.introUrl) {
    logger.info('No question intro found, skipping...')
    stepFinished()
    return
  }

  logger.info('Play question intro')
  await setAvatarPosition(
    currentSubtest.value!.metadata?.questionAvatarPosition,
    currentSubtest.value!.metadata?.questionAvatarSize
  )

  avatarDelay.value = 1
  setAvatarAudio(currentQuestion.value!.introUrl)
  showQuestionCue.value = true
}

async function playQuestion(): Promise<void> {
  if (answerGiven.value) {
    stepFinished()
    return
  }

  logger.info('Play question')
  await setAvatarPosition(
    currentSubtest.value!.metadata?.questionAvatarPosition,
    currentSubtest.value!.metadata?.questionAvatarSize
  )

  avatarDelay.value = 0.5
  showQuestionCue.value = true
  setAvatarAudio(currentQuestion.value!.questionUrl)
}

async function playAvatarPracticeAnswer(): Promise<void> {
  if (!currentQuestion.value!.avatarAnswerUrl) return
  avatarDelay.value = 1
  forceCueActiveState.value = true
  setAvatarAudio(currentQuestion.value!.avatarAnswerUrl)
  questionCue.value?.start()
  if (showSelectionComponent.value) {
    awaitingAvatarSelection.value = true
    avatarDelay.value = 3
  }

  avatarCallback.value = avatarPracticeAnswerGiven
}

function avatarPracticeAnswerGiven() {
  //If avatar is still selecting or is still talking hold off before continuing
  if (awaitingAvatarSelection.value || !avatarFinished.value) return
  dataStore.sendRewardUpdateToApi(1)
  avatarCallback.value = null
  awaitingAvatarSelection.value = false
  setManagedTimeout(() => {
    questionCue.value?.stop()
    forceCueActiveState.value = false
    answerGiven.value = true
    startsThisSubtest.value += 1
    showAssetComponent.value = false
    showSelectionComponent.value = false
    avatarDelay.value = 0.5
    avatarCallback.value = stepFinished

    showQuestionReward()
  }, 1000) // Slight delay after the avatars answer
}

const awaitingAvatarSelection = ref(false)

async function repositionAvatar() {
  if (
    avatarOptions.value !== null &&
    currentSubtest.value!.metadata?.questionAvatarPosition === avatarOptions.value.position
  ) {
    transitionAvatarCallback()
  } else {
    await setAvatarPosition(
      currentSubtest.value!.metadata?.questionAvatarPosition,
      currentSubtest.value!.metadata?.questionAvatarSize
    )

    logger.info('Repositioning avatar')
    effectsStore.setAvatarTransitionCallback(null, transitionAvatarCallback)
  }
}

function transitionAvatarCallback() {
  if (!avatarCallback.value) {
    logger.error('Avatar has no callback')
    return
  }
  logger.info('Avatar callback called in test')
  avatarCallback.value()
  effectsStore.setAvatarTransitionCallback(null, null)
}

async function setAvatarPosition(position: AvatarPosition, size: AvatarSize): Promise<void> {
  if (avatarOptions.value !== null && position === avatarOptions.value.position) return
  logger.info('Set avatar to position: ', position, ' and size: ', size)

  avatarOptions.value = {
    ...(avatarOptions.value as AvatarOptions),
    position: position,
    size: size
  }
  avatar.value?.updateOptions(avatarOptions.value)
}

async function setAvatarAudio(audio: string): Promise<void> {
  avatarOptions.value = {
    ...(avatarOptions.value as AvatarOptions),
    audio: audio
  }
  avatar.value?.updateOptions(avatarOptions.value)
  avatarFinished.value = false
}

//____________________ AUDIO RECORDING FUNCTIONS ____________________

function initiateRecording(): void {
  if (!isAvatarsTurn.value) {
    recorder.value?.initiateRecording()
  }
  stepFinished()
}

function setRecording(): void {
  logger.info('Set recording')
  record.value = currentSubtest.value?.answerType === 'recording'
}

function waitForAudioAnswer(): void {
  if (isAvatarsTurn.value) {
    playAvatarPracticeAnswer()
    return
  }
  logger.info('Waiting for audio answer')
  waitingForAnswer.value = true
}

function onAudioAnswerGiven(answered: boolean): void {
  logger.info('Audio answer given ', answered)
  waitingForAnswer.value = false
  answerGiven.value = answered
  startsThisSubtest.value += 1
  showAssetComponent.value = false

  showQuestionReward()
}

//____________________ IMAGE SELECTION FUNCTIONS ____________________
async function showSelection(): Promise<void> {
  logger.info('Show selection')
  if (
    avatarOptions.value !== null &&
    currentSubtest.value!.metadata?.questionAvatarPosition === avatarOptions.value.position
  ) {
    currentOptions.value = currentQuestion.value!.options as OptionsEntity[]
    showSelectionComponent.value = true
    stepFinished()
  } else {
    await setAvatarPosition(
      currentSubtest.value!.metadata?.questionAvatarPosition,
      currentSubtest.value!.metadata?.questionAvatarSize
    )

    currentOptions.value = currentQuestion.value!.options as OptionsEntity[]
    // Display first image selection after the avatar has been repositioned
    effectsStore.setAvatarTransitionCallback(null, showFirstSelection)
  }
}

function showFirstSelection() {
  effectsStore.setAvatarTransitionCallback(null, null)
  showSelectionComponent.value = true
  stepFinished()
}

function waitForSelection(): void {
  if (isAvatarsTurn.value) {
    playAvatarPracticeAnswer()
    return
  }
  logger.info('Waiting for selection')
  waitingForAnswer.value = true
}

async function onSelection(answered: boolean): Promise<void> {
  logger.info('Selection made')
  if (isAvatarsTurn.value) {
    awaitingAvatarSelection.value = false
    avatarPracticeAnswerGiven()
    return
  }

  waitingForAnswer.value = false
  // Awaits for the prompt animation to finish before continuing for smoother transition
  if (isAvatarPrompting.value) {
    try {
      await waitForPromptFinishing()
    } catch (error) {
      dataStore.setScreenerError(
        'UserUnresponsive',
        'The user may need help proceeding with the screener, their response timing is longer than expected.'
      )
      return
    }
  }

  answerGiven.value = answered
  showSelectionComponent.value = false
  startsThisSubtest.value += 1

  // wait for animation to finish before proceeding
  setManagedTimeout(() => {
    avatarCallback.value = stepFinished // reset the callback to the default
    showQuestionReward()
  }, slideTransitionDuration)
}

//____________________ VIDEO SELECTION FUNCTIONS ____________________

async function showAsset(assetType: AssetType): Promise<void> {
  logger.info('Show asset', assetType)

  await setAvatarPosition(
    currentSubtest.value!.metadata?.questionAvatarPosition,
    currentSubtest.value!.metadata?.questionAvatarSize
  )

  let assetToggled = showAssetComponent.value //previous state of asset component

  showAssetComponent.value = false
  setManagedTimeout(
    () => {
      avatarDelay.value = 0
      currentAssetType.value = assetType
      currentAssetUrl.value = currentQuestion.value?.assetUrl || ''
      showAssetComponent.value = true

      if (assetType === 'video') {
        videoEndedCallback.value = stepFinished // reset the callback to the default
        playVideo(true)
      } else stepFinished()
    },
    assetToggled ? videoTransitionDuration * 2 : 0
  )
}

function onVideoEnded() {
  logger.info('Video ended')
  playVideo(false)
  videoEndedCallback.value()
}

function playVideo(value: boolean) {
  logger.info('Toggle video', value)
  playVideoRef.value = value
}

// _____________________ QUESTIONNAIRE FUNCTIONS _____________________

function showQuestionnaire() {
  logger.info('Showing questionnaire')
  showWellbeingProgress.value = true
  showQuestionnaireComponent.value = true
  avatarDelay.value = 0

  stepFinished()
}

function onQuestionnaireAnswerGiven() {
  logger.info('Questionnaire answered')
  waitingForAnswer.value = false
  avatar.value?.stop()
  showQuestionnaireComponent.value = false
  setManagedTimeout(() => {
    stepFinished()
  }, fadeComponentDuration / 2)
}

//____________________ PROMPT FUNCTIONS ____________________

function playPrompt(): void {
  logger.info('Play prompt')
  isPlayingPrompt.value = true
  waitingForAnswer.value = false
  steps[sequenceName.value].promptFunction()
}

function playSelectionPrompt() {
  nextTick(() => {
    logger.info('Play audio prompt')
    effectsStore.queueAvatarAnimation(avatarPromptAnimation, false)
    avatarDelay.value = 0
    const avatarAudioUrl = getPromptOrOutroUrl(
      (currentQuestion.value!.promptUrl as string) || 'random',
      'prompt'
    )
    setAvatarAudio(avatarAudioUrl)
    waitingForAnswer.value = true
    avatarCallback.value = onPromptFinished
  })
}

function playAudioPromptWithQuestion() {
  logger.info('Play audio prompt with question')
  if (sequenceName.value === 'image-recording' && avatarPromptAnimation) {
    effectsStore.queueAvatarAnimation(avatarPromptAnimation, false)
  }

  avatarDelay.value = 1

  const avatarAudioUrl = getPromptOrOutroUrl(
    (currentQuestion.value!.promptUrl as string) || 'random',
    'prompt'
  )
  setAvatarAudio(avatarAudioUrl)
  avatarCallback.value = playQuestionAsPrompt
}

function playVideoPrompt() {
  logger.info('Play video prompt')
  if (avatarPromptAnimation) {
    effectsStore.queueAvatarAnimation(avatarPromptAnimation, false)
  }
  // Video prompt will be preceded by an audio prompt followed by the video and then the question
  avatarCallback.value = playVideo.bind(null, true) // after the audio prompt we want to play the video
  videoEndedCallback.value = playQuestionAsPrompt // after the video we want to play the question
  const avatarAudioUrl =
    getPromptOrOutroUrl(currentQuestion.value!.promptUrl as string, 'prompt') || '' // we set the audio prompt which will trigger the avatar to play the audio prompt
  setAvatarAudio(avatarAudioUrl)
}

function playQuestionAsPrompt() {
  logger.info('Play question as prompt')
  avatarCallback.value = onPromptFinished
  const avatarAudioUrl = currentQuestion.value!.questionUrl
  setAvatarAudio(avatarAudioUrl)
}

function onPromptFinished() {
  logger.info('Prompt finished')
  isPlayingPrompt.value = false
  waitingForAnswer.value = true
  avatarCallback.value = stepFinished // reset the callback to the default
}

//____________________ OUTRO AND FINAL STEPS FUNCTIONS ____________________

async function playOutro(): Promise<void> {
  // If it's the last question of the subtest, play the subtest outro
  if (!currentQuestion.value?.isPracticeQuestion && isLastQuestion.value) {
    playSubtestOutro()
    // Otherwise, play the question outro
  } else {
    playQuestionOutro()
  }
}

function playQuestionOutro() {
  let outroUrl: string = currentQuestion.value!.answerGivenOutroUrl

  if (!answerGiven.value && currentQuestion.value?.avatarAnswerUrl?.endsWith('_OA.mp3')) {
    outroUrl = currentQuestion.value?.avatarAnswerUrl
  }
  const avatarAudioUrl = getPromptOrOutroUrl(outroUrl, 'outro')
  setAvatarAudio(avatarAudioUrl)
}

async function playSubtestOutro() {
  currentScene.value = 'RewardScreen'
  closeAllComponents()
  setBlur(false, false)

  await setAvatarPosition('hidden', 'xs')

  effectsStore.setNextBackgroundImage(screenerData.value?.metadata?.rewardBackground || 'Blank')
  effectsStore.setAvatarTransitionCallback(loadAvatarRewardPosition, null)
}

async function loadAvatarRewardPosition() {
  dataStore.resetDemoSkippingTo()

  currentScene.value = screenerData.value?.metadata?.rewardBackground
    ? screenerData.value?.metadata?.rewardBackground.split('.')[0]
    : 'RewardScreen'
  effectsStore.applyBackgroundImage()
  showSubtestRewardComponent.value = true
  sceneAnimationPlaying.value = true
  await nextTick()

  //Gets the path for the avatar to walk from the current waypoint to the next waypoint
  const walkingPositions = await subtestRewardComponent.value?.getPathForAvatar()
  if (!walkingPositions) {
    logger.error('No walking positions found progressive map failed to load')
    stepFinished() // Skip the reward animation to next subtest
    return
  }
  effectsStore.setAvatarTransitionCallback(null, null)

  //Sets up the avatar ready for the transition in positioning and passes in the path to walk
  avatar.value?.setupWalkingPositions(walkingPositions)
  // Triggers the change in position starting the transition out from the previous scene and in at the path starting location
  avatarOptions.value = {
    ...(avatarOptions.value as AvatarOptions),
    position: 'fixed',
    size: 'xs'
  }
  setAvatarAudio(currentSubtest.value!.outroUrl)
}

function startSubtestRewardAnimation(): void {
  effectsStore.setRewardAnimationTrigger(true)
  effectsStore.setAvatarTransitionCallback(null, null)
  avatarCallback.value = stepFinished
}

async function playScreenerOutro() {
  activeScreenerStore.sendSectionChange({ section: 'ScreenerOutro' })
  currentScene.value = 'Outro'
  effectsStore.setLoadedSceneAnimations(screenerData.value?.metadata?.outro?.sceneAnimations || [])
  effectsStore.setAnimationTriggers(screenerData.value?.metadata?.outro?.animationTriggers || [])
  effectsStore.setNextBackgroundImage(screenerData.value?.metadata?.outro?.background || '')

  await setAvatarPosition(
    screenerData.value?.metadata?.outro?.avatarPlacement?.position,
    screenerData.value?.metadata?.outro?.avatarPlacement?.size
  )

  const outroFunction =
    introSequenceFunction[screenerData.value?.metadata?.intro?.sequenceName]?.outro
  if (outroFunction) {
    outroFunction()
    return
  }
  setAvatarAudio(screenerData.value?.metadata?.outro?.url)

  logger.info('Play screener outro')

  effectsStore.setNextBackgroundImage(screenerData.value?.metadata?.outro?.background || '')
  effectsStore.setAvatarTransitionCallback(setOutroBackground)
  avatarCallback.value = finalizeScreening
}

function setOutroBackground() {
  changeBackgroundVolume(false, false)
  closeAllComponents()
  setBackgroundImage()
}

function playStarPopGame() {
  const allowedSubtestKeys = ['nonword', 'verbMorphology']

  const isGameAllowed = allowedSubtestKeys.includes(currentSubtest.value!.key)

  if (isGameAllowed && !currentQuestion.value?.isPracticeQuestion && isLastQuestion.value) {
    effectsStore.setNextBackgroundImage('StarPopBG.png')
    dataStore.setStarPopGameState(true)

    effectsStore.setAvatarTransitionCallback(() => {
      setBackgroundImage()
      hideAvatar.value = true
      showStarPopGameComponent.value = true
      effectsStore.setAvatarTransitionCallback(null, null)
    }, null)
    effectsStore.queueAvatarAnimation(effectsStore.getTransitionAnimation('OUT'), true)
  } else {
    stepFinished()
  }
}

function onStarPopGameFinished() {
  avatarOptions.value!.audio = ''
  hideAvatar.value = false
  showStarPopGameComponent.value = false
  stepFinished()
  dataStore.setStarPopGameState(false)
}

function finalStep() {
  logger.info('Final step')
  if (!currentQuestion.value?.isPracticeQuestion && isLastQuestion.value) finishSubtest()
  else nextQuestion()
}

function showQuestionReward(): void {
  if (
    activityStore.screenClickNeeded() === true ||
    (isLastQuestion.value && !currentQuestion.value?.isPracticeQuestion)
  ) {
    showClickableQuestionReward()
    return
  }

  logger.info('Show question reward')
  showQuestionRewardComponent.value = true
}

function showClickableQuestionReward(): void {
  logger.info('Show clickable question reward', isLastQuestion.value)
  questionRewardClickable.value = true
  showQuestionRewardComponent.value = true
}

function showButton(): void {
  showNextQuestionBtn.value = true
}

function onProceedTriggered(): void {
  recorder.value?.skipWaitingForAnswer()
}

function nextQuestion(): void {
  logger.info('Next question button clicked')
  showNextQuestionBtn.value = false
  dataStore.callSetNextQuestion()
  resetSequence()
  stepFinished()
}

function finishSubtest(): void {
  logger.info('Finish subtest')
  //If last subtest, play the screener outro after the subtest outro

  if (dataStore.checkIfLastSubtest(currentSubtest.value!)) playScreenerOutro()
  else nextQuestion()
}

function getPromptOrOutroUrl(url: string, type: 'prompt' | 'outro'): string {
  if (!url) return ''

  if (url.toLowerCase() === 'random' && type === 'prompt') {
    // Random number between 1 and 4
    const randomNumber =
      Math.floor(Math.random() * screenerData.value!.metadata?.randomPromptsNumber || 1) + 1
    const fileName = `RANDOM_PROMPT_${randomNumber}.mp3`
    return fileName
  }

  if (url.toLowerCase() === 'random' && type === 'outro') {
    const typeOfOutro = answerGiven.value ? 'POSITIVE' : 'NEUTRAL'
    let randomNumber = 1
    if (typeOfOutro === 'POSITIVE') {
      randomNumber =
        Math.floor(Math.random() * screenerData.value!.metadata?.randomPositiveOutrosNumber) + 1
    } else {
      randomNumber =
        Math.floor(Math.random() * screenerData.value!.metadata?.randomNeutralOutrosNumber) + 1
    }

    const fileName = `RANDOM_${typeOfOutro}_OUTRO_${randomNumber}.mp3`
    return fileName
  }

  return url
}

function setBlur(value: boolean, callStepFinished: boolean = true): void {
  logger.info('setBlur', value)
  effectsStore.setBackgroundBlur(value)

  if (callStepFinished) stepFinished()
}

// Fades background music in or out and adds a pause before proceeding if necessary
function changeBackgroundVolume(isFaded: boolean, proceed: boolean = false): void {
  const fadeOutDuration: number = parseFloat((fadeDuration.value / 2).toFixed(2))
  const pauseDuration =
    backgroundTrackVolume.value === backgroundVolumeFadeLevel.value
      ? 0
      : fadeOutDuration * 1000 + 500
  if (isFaded) {
    backgroundTrackVolume.value = backgroundVolumeFadeLevel.value
    logger.info('Faded background music')
  } else {
    backgroundTrackVolume.value = backgroundVolumeLevel.value
  }

  if (proceed) {
    setManagedTimeout(() => {
      stepFinished()
    }, pauseDuration)
  }
}

// Wait until the avatar prompt animation is finished
function waitForPromptFinishing(): Promise<void> {
  return new Promise((resolve, reject) => {
    if (!avatarPromptAnimation) {
      resolve()
      return
    }

    const isAnimationDone = () =>
      avatarAnimationState.value !== avatarPromptAnimation &&
      !avatarAnimationQueue.value.includes(avatarPromptAnimation)

    const stopWatching = watch(
      [avatarAnimationState, () => [...avatarAnimationQueue.value]],
      () => {
        if (isAnimationDone()) {
          stopWatching()
          resolve()
        }
      },
      { immediate: true }
    )

    setManagedTimeout(() => {
      stopWatching()
      reject(new Error('Timeout waiting for avatar animation to finish'))
    }, 10000)
  })
}

function onQuestionRewardCollected() {
  logger.info('Question reward collected')
  showQuestionRewardComponent.value = false
  questionRewardClickable.value = false
  rewardCollectedCallback.value()

  rewardCollectedCallback.value = stepFinished // reset the callback to the default
}

function closeAllComponents(): void {
  showQuestionnaireComponent.value = false
  showQuestionCue.value = false
  showSelectionComponent.value = false
  showAssetComponent.value = false
}

// _____________________ INTRO SEQUENCES _____________________

async function saltScreenerIntroStep1(): Promise<void> {
  logger.info('Play screener intro step 1')
  changeBackgroundVolume(false, false)
  setBlur(false, false)
  activeScreenerStore.sendSectionChange({ section: 'ScreenerIntro' })
  currentScene.value = 'Intro'

  await setAvatarPosition(
    screenerData.value?.metadata?.intro?.avatarPlacement?.position,
    screenerData.value?.metadata?.intro?.avatarPlacement?.size
  )
  effectsStore.setLoadedSceneAnimations(screenerData.value?.metadata?.intro?.sceneAnimations || [])
  effectsStore.applyBackgroundImage(screenerData.value?.metadata?.intro?.background || '')

  if (rewardsDisabled.value) {
    avatarCallback.value = saltScreenerIntroStep2
  } else {
    avatarCallback.value = showClickableQuestionReward
    rewardCollectedCallback.value = saltScreenerIntroStep2
  }

  setAvatarAudio(screenerData.value?.metadata?.intro?.url[0])
  effectsStore.setAnimationTriggers(screenerData.value?.metadata?.intro?.animationTriggers || [])
}

async function saltScreenerIntroStep2(): Promise<void> {
  logger.info('Play screener intro step 2')
  await setAvatarPosition('bottom-left', 'sm')
  avatarCallback.value = saltScreenerIntroStep3
  setAvatarAudio(screenerData.value?.metadata?.intro?.url[1])
}

async function saltScreenerIntroStep3(): Promise<void> {
  logger.info('Play screener intro step 3')
  showQuestionCue.value = true
  nextTick(async () => {
    await questionCue.value?.demoShush()
    avatarCallback.value = saltScreenerIntroStep4
    setAvatarAudio(screenerData.value?.metadata?.intro?.url[2])
  })
}

async function saltScreenerIntroStep4(): Promise<void> {
  logger.info('Play screener intro step 4')
  showQuestionCue.value = false
  avatarCallback.value = saltScreenerIntroStep5
  setAvatarAudio(screenerData.value?.metadata?.intro?.url[3])
}

async function saltScreenerIntroStep5(): Promise<void> {
  logger.info('Play screener intro step 5')
  forceCueActiveState.value = true
  showQuestionCue.value = true

  nextTick(async () => {
    await questionCue.value?.demoRecording()
    logger.info('step 5: resolved demoRecording')
    activeScreenerStore.sendSectionChange({ section: 'ScreenerQuestionsView' })

    avatarCallback.value = () => {
      introSequencePlaying.value = false
      screenerIntroPlayed.value = true
      showQuestionCue.value = false
      avatarCallback.value = stepFinished
      forceCueActiveState.value = false
      dataStore.setScreenerIntroPlayed(true)
      resetSequence()
      stepFinished()
    }
    setAvatarAudio(screenerData.value?.metadata?.intro?.url[4])
  })
}

//Wellbeing screener intro demo sequence
const activeWellbeingSlide: Ref<number> = ref(0)
const activeWellbeingTimestamps: Ref<QuestionTimestamp[]> = ref([])

async function wellbeingScreenerIntro() {
  activeScreenerStore.sendSectionChange({ section: 'ScreenerIntro' })
  avatarDelay.value = 0.1
  const currentSlide = screenerData.value?.metadata?.intro?.section[0]
  activeWellbeingTimestamps.value = await dataStore.fetchHighlightingTimestamps(currentSlide.url)
  setAvatarAudio(currentSlide.url)
  showWellbeingSlides.value = true
  avatarCallback.value = wellbeingScreenerIntroSteps
}

async function wellbeingScreenerIntroSteps() {
  const nextSlide = activeWellbeingSlide.value + 1
  const currentSlide = screenerData.value?.metadata?.intro?.section[nextSlide]
  activeWellbeingTimestamps.value = await dataStore.fetchHighlightingTimestamps(currentSlide.url)
  activeWellbeingSlide.value = nextSlide

  setAvatarAudio(currentSlide.url)
  if (activeWellbeingSlide.value === 3) {
    avatarCallback.value = wellbeingScreenerIntroFinished
  }
}

function wellbeingScreenerIntroFinished() {
  activeScreenerStore.sendSectionChange({ section: 'ScreenerQuestionsView' })
  avatarDelay.value = 1
  showWellbeingSlides.value = false
  avatarCallback.value = stepFinished
  stepFinished()
}

function wellbeingScreenerOutro() {
  activeScreenerStore.sendSectionChange({ section: 'ScreenerOutro' })
  showWellbeingProgress.value = false
  closeAllComponents()
  fadeComponentDelay = 0
  finalizeScreening()
}
</script>

<style lang="scss" scoped>
.screenerQuestionsWrapper {
  opacity: 1;
  transition: opacity v-bind('`${fadeComponentDuration}ms`') v-bind('`${fadeComponentDelay}ms`');
  width: 100%;
  height: 100%;

  &.hide {
    opacity: 0;
  }
}

.bounce-enter-active {
  position: fixed;
  @include animation(bounce-in v-bind('`${avatarTransitionDuration}ms`'));
}
.bounce-leave-active {
  position: fixed;
  @include animation(bounce-in reverse v-bind('`${avatarTransitionDuration}ms`'));
}

.video-fade-leave-active {
  opacity: 1;
  transition: opacity v-bind('`${videoTransitionDuration}ms`');
}

.video-fade-leave {
  opacity: 1;
}

.video-fade-leave-to {
  opacity: 0;
}

@include keyframes(bounce-in) {
  0% {
    transform: scale(0.3);
    opacity: 0.75;
  }
  50% {
    transform: scale(1.25);
    opacity: 0.9;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

.slide-fade-leave-active,
.slide-fade-enter-active {
  transition: all v-bind('`${slideTransitionDuration}ms`') ease-out;
}

.slide-fade-enter-from {
  transform: translateX(100px);
  opacity: 0;
}

.slide-fade-leave-to {
  transform: translateX(-100px);
  opacity: 0;
}

.fade-enter-active,
.fade-leave-active {
  transition: all 1s ease-out;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
